/**
 * ADR-0231 — formatting helpers for the Trust engine (shadow) panel.
 * Pure; unit-tested in __tests__/trust-helpers.test.ts.
 */

import type { EvidenceTier, VerdictLabel } from '@sill/types';

export const TIER_LABEL: Readonly<Record<EvidenceTier, string>> = {
  edge_attested: 'Edge-attested (signed Worker)',
  install_attested: 'Install-attested (signed plugin)',
  anonymous: 'Anonymous (unsigned)',
  sill_observed: 'Sill-observed (credential on a Sill surface)',
};

export const LABEL_LABEL: Readonly<Record<VerdictLabel, string>> = {
  verified: 'Verified',
  unverified: 'Unverified',
  likely_impersonator: 'Likely impersonator',
};

export const TIERS: readonly EvidenceTier[] = ['edge_attested', 'install_attested', 'anonymous', 'sill_observed'];
export const LABELS: readonly VerdictLabel[] = ['verified', 'unverified', 'likely_impersonator'];

export const COUNTERFACTUAL_NOTE =
  'Counterfactual: historical anonymous traffic evaluated as if attested; not a measurement of live accuracy.';

/**
 * A rate in [0, 1] as a percentage with precision suited to small
 * values: 0 → "0%", < 0.01% → "<0.01%", < 1% → 2 decimals, else 1.
 */
export function formatRate(r: number | null | undefined): string {
  if (r === null || r === undefined || !Number.isFinite(r)) return '—';
  if (r <= 0) return '0%';
  const pct = r * 100;
  if (pct < 0.01) return '<0.01%';
  if (pct < 1) return `${pct.toFixed(2)}%`;
  if (pct >= 100) return '100%';
  return `${pct.toFixed(1)}%`;
}

/** Share of `n` in `total`, as a rate (0 when total is 0). */
export function share(n: number, total: number): number {
  return total > 0 ? n / total : 0;
}
